import { useQuery, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CLINIC_SETTINGS_SINGLETON_ID } from "@/hooks/useClinicSettings";

export type ReferencePatient = {
  id: string;
  name: string;
  cpf: string | null;
};

export type ReferenceProfessional = {
  id: string;
  authUserId: string | null;
  name: string;
  nickname: string | null;
  email: string | null;
  role: string | null;
  blocked: boolean;
};

export const referenceQueryKeys = {
  patients: (clinicId: string) => ["reference", "patients", clinicId] as const,
  professionals: (clinicId: string) => ["reference", "professionals", clinicId] as const,
  services: (clinicId: string) => ["reference", "services", clinicId] as const,
};

const REFERENCE_STALE_TIME_MS = 1000 * 60 * 10; // 10 min — catálogo profissionais (estável)
const REFERENCE_PATIENTS_STALE_MS = 1000 * 60 * 3; // 3 min — pacientes podem ser cadastrados ao longo do dia

export const normalizeReferencePatient = (row: { id: string; name: string; cpf?: string | null }): ReferencePatient => ({
  id: row.id,
  name: row.name?.trim() || "Paciente",
  cpf: row.cpf ?? null,
});

export const normalizeReferenceProfessional = (row: {
  id: string;
  user_id?: string | null;
  full_name?: string | null;
  nickname?: string | null;
  email?: string | null;
  role?: string | null;
  blocked?: boolean | null;
}): ReferenceProfessional => ({
  id: row.id,
  authUserId: row.user_id ?? null,
  name: row.nickname?.trim() || row.full_name?.trim() || row.email?.split("@")[0] || "Profissional",
  nickname: row.nickname ?? null,
  email: row.email ?? null,
  role: row.role ?? null,
  blocked: row.blocked ?? false,
});

export async function fetchReferencePatients(): Promise<ReferencePatient[]> {
  const { data, error } = await supabase.from("patients").select("id,name,cpf").order("name");
  if (error) throw error;
  return ((data ?? []) as Array<{ id: string; name: string; cpf?: string | null }>).map(normalizeReferencePatient);
}

export async function fetchReferenceProfessionals(): Promise<ReferenceProfessional[]> {
  const { data, error } = await supabase
    .from("v_professionals")
    .select("id, user_id, full_name, nickname, email, role, blocked")
    .order("full_name", { ascending: true });

  if (error) throw error;
  return ((data ?? []) as Array<{
    id: string;
    user_id?: string | null;
    full_name?: string | null;
    nickname?: string | null;
    email?: string | null;
    role?: string | null;
    blocked?: boolean | null;
  }>).map(normalizeReferenceProfessional);
}

export function useReferencePatients(clinicId = CLINIC_SETTINGS_SINGLETON_ID, enabled = true) {
  return useQuery({
    queryKey: referenceQueryKeys.patients(clinicId),
    queryFn: fetchReferencePatients,
    staleTime: REFERENCE_PATIENTS_STALE_MS,
    enabled,
  });
}

export function useReferenceProfessionals(clinicId = CLINIC_SETTINGS_SINGLETON_ID, enabled = true) {
  return useQuery({
    queryKey: referenceQueryKeys.professionals(clinicId),
    queryFn: fetchReferenceProfessionals,
    staleTime: REFERENCE_STALE_TIME_MS,
    enabled,
  });
}

export async function prefetchReferenceCatalogs(queryClient: QueryClient, clinicId = CLINIC_SETTINGS_SINGLETON_ID) {
  await Promise.allSettled([
    queryClient.prefetchQuery({ queryKey: referenceQueryKeys.patients(clinicId), queryFn: fetchReferencePatients, staleTime: REFERENCE_PATIENTS_STALE_MS }),
    queryClient.prefetchQuery({ queryKey: referenceQueryKeys.professionals(clinicId), queryFn: fetchReferenceProfessionals, staleTime: REFERENCE_STALE_TIME_MS }),
  ]);
}
